import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '@phase2/outline-core';
import { customElement, property, query } from 'lit/decorators.js';

import componentStyles from './pc-accordion-content.css.lit';
import { debounce } from '../../../../utilities/debounce';

interface AnimationStyles {
  transitionDuration: string;
  animationName: string;
}

/**
 * The Accordion Content component
 * @element pc-accordion-content
 */
@customElement('pc-accordion-content')
export class PCAccordionContent extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'aria-label' })
  ariaLabel: string;

  /**
   * Wether the panel is active/open.
   * Controlled by parent accordion component.
   */
  @property({ type: Boolean, reflect: true })
  active: boolean;

  @query('.accordion-content')
  node!: HTMLElement;

  @property({ type: Boolean, reflect: true, attribute: 'default-active' })
  defaultActive: boolean;

  originalStyles: AnimationStyles;
  height = 0;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', debounce(this._handleResize, 50));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', debounce(this._handleResize, 50));
  }

  _handleContentChange = () => {
    if (this.active) {
      this.removeAttribute('is-ready');
      this._blockAnimations();

      setTimeout(() => {
        this._setContentHeight();
        this._restoreAnimations();
        this.setAttribute('is-ready', 'true');
      }, 500);
    }
  };

  render(): TemplateResult {
    return html`
      <section class="accordion-content">
        <div class="content" @contentChange=${this._handleContentChange}>
          <slot></slot>
        </div>
      </section>
    `;
  }

  protected updated(): void {
    const isOpen = this.active ?? this.defaultActive;
    this.setAttribute('aria-hidden', `${!isOpen}`);
    const animations = this.defaultActive
      ? this._runDefaultAnimation
      : this._runAnimations;

    animations(isOpen);
  }

  _runAnimations = (isOpen: boolean) => {
    if (isOpen) {
      this._blockAnimations();
      this._setContentHeight();
      this._restoreAnimations();
      setTimeout(() => this.setAttribute('is-ready', 'true'));
    } else {
      this.removeAttribute('is-ready');
    }
  };

  _runDefaultAnimation = (isOpen: boolean) => {
    if (isOpen) {
      this._blockAnimations();
    } else {
      this._setContentHeight();
      this._restoreAnimations();
      this.setAttribute('is-ready', 'true');

      setTimeout(() => {
        this.removeAttribute('is-ready');
        this.defaultActive = false;
      });
    }
  };

  _handleResize = () => {
    if (this.active) {
      this._blockAnimations();
      this.removeAttribute('is-ready');
      this._setContentHeight();
      this._restoreAnimations();
      this.setAttribute('is-ready', 'true');
    }
  };

  // Block any animations/transitions so the element renders at its full dimensions.
  _blockAnimations() {
    this.node.classList.add('preview');

    this.originalStyles = this.originalStyles || {
      transitionDuration: this.node.style.transitionDuration,
      animationName: this.node.style.animationName,
    };

    this.node.style.transitionDuration = '0s';
    this.node.style.animationName = 'none';
  }

  _restoreAnimations() {
    this.node.style.transitionDuration = this.originalStyles.transitionDuration;
    this.node.style.animationName = this.originalStyles.animationName;

    this.node.classList.remove('preview');
  }

  _setContentHeight() {
    const rect = this.node.getBoundingClientRect();
    this.style.setProperty('--accordion-content-height', `${rect.height}px`);
    this.height = rect.height;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-accordion-content': PCAccordionContent;
  }
}
